import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { formatEUR } from '../../lib/format';
import {
  Empty,
  thCls,
  thNum,
  tdCls,
  tdNum,
  type Cust,
  type Exposure,
} from '../../components/collection/collectionShared';

/** Comptes clients — each customer's exposure, broken down, and what blocks it. */
interface Row extends Cust {
  limit: number | null;
  exposure: Exposure;
  paused: boolean;
  promise: { date: string } | null;
  manualBlock: { reason: string } | null;
}

export default function CollectionAccounts() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    apiClient
      .get<Row[]>('/collection/customers')
      .then((r) => setRows(r.data))
      .catch((e) => {
        setRows([]);
        showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const terms = (r: Row) =>
    r.type === 'B2B_ACCOUNT'
      ? `${r.terms ?? ''}${r.mode ? ` · ${t(`collection.mode.${r.mode}`)}` : ''}`
      : t(r.type === 'B2B_PREPAID' ? 'collection.accounts.prepaid' : 'collection.accounts.atOrder');
  return (
    <div>
      <PageHeader
        title={t('collection.accounts.title')}
        description={t('collection.accounts.description')}
      />
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-4 py-2 text-sm text-muted-foreground">
          {rows
            ? t('collection.accounts.count', { count: rows.length })
            : t('collection.common.loading')}
        </div>
        {rows === null ? null : rows.length === 0 ? (
          <Empty>{t('collection.accounts.empty')}</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className={thCls}>{t('collection.col.customer')}</th>
                  <th className={thCls}>{t('collection.col.terms')}</th>
                  <th className={thNum}>{t('collection.col.exposure')}</th>
                  <th className={thNum}>{t('collection.col.limit')}</th>
                  <th className={thNum}>{t('collection.col.overdue')}</th>
                  <th className={thCls}>{t('collection.col.state')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
                    onClick={() => navigate(`/collection/accounts/${r.id}`)}
                  >
                    <td className={tdCls}>
                      <b>{r.name}</b>
                      <div className="text-xs text-muted-foreground">
                        {r.code ? `${r.code} · ` : ''}
                        {t(`collection.type.${r.type}`)}
                      </div>
                    </td>
                    <td className={tdCls}>{terms(r)}</td>
                    <td className={tdNum}>{formatEUR(r.exposure.total)}</td>
                    <td className={tdNum}>
                      {r.type === 'B2B_ACCOUNT'
                        ? r.limit == null
                          ? t('collection.accounts.unlimited')
                          : formatEUR(r.limit)
                        : '—'}
                    </td>
                    <td className={tdNum}>
                      {r.exposure.overdueAmount ? (
                        <>
                          <b className="text-destructive">{formatEUR(r.exposure.overdueAmount)}</b>
                          <div className="text-xs text-muted-foreground">
                            {t('collection.accounts.maxLate', { n: r.exposure.maxLate })}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={tdCls}>
                      {r.exposure.blocked ? (
                        <Badge variant="destructive">{t('collection.accounts.blocked')}</Badge>
                      ) : r.paused ? (
                        <Badge variant="default">{t('collection.accounts.paused')}</Badge>
                      ) : r.promise ? (
                        <Badge variant="default">{t('collection.accounts.promise')}</Badge>
                      ) : (
                        <Badge variant="success">{t('collection.accounts.active')}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
